/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutableCallback } from './useMutableCallback';
import { useRef } from 'react';

/**
 * Ensure the click is valid by checking the movement and duration.
 */
export const useGuardClick = (
  onClick: () => void,
  opts?: { maxDuration?: number; thresholdPx?: number },
) => {
  const { thresholdPx = 6, maxDuration = 500 } = { ...opts };
  const downRef = useRef<{ x: number; y: number; t: number; id: number | null } | null>(null);
  const draggedRef = useRef(false);

  const onPointerDown = useMutableCallback((e: any) => {
    const { clientX: x, clientY: y, pointerId } = e;
    // capture so we get the pointerup even if the cursor leaves the mesh
    (e.target as any).setPointerCapture?.(pointerId);

    downRef.current = { x, y, t: performance.now(), id: pointerId };
    draggedRef.current = false;
  });

  const onPointerMove = useMutableCallback((e: any) => {
    if (!downRef.current) return;
    // only track the active pointer
    if (downRef.current.id != null && e.pointerId !== downRef.current.id) return;

    const dx = e.clientX - downRef.current.x;
    const dy = e.clientY - downRef.current.y;
    if (!draggedRef.current) {
      // once we exceed threshold, we mark as dragged
      draggedRef.current = Math.hypot(dx, dy) > thresholdPx;
    }
  });

  const onPointerUp = useMutableCallback((e: any) => {
    if (!downRef.current) return;
    if (downRef.current.id != null && e.pointerId !== downRef.current.id) return;

    (e.target as any).releasePointerCapture?.(e.pointerId);

    const dt = performance.now() - downRef.current.t;
    const dx = e.clientX - downRef.current.x;
    const dy = e.clientY - downRef.current.y;
    const moved = Math.hypot(dx, dy);

    const isClick =
      !draggedRef.current &&
      moved <= thresholdPx &&
      (maxDuration ? dt <= maxDuration : true) &&
      e.button === 0; // left click (optional)

    downRef.current = null;
    draggedRef.current = false;

    if (isClick) {
      // fire the parent's onClick
      onClick?.();
      // prevent any other click handlers from double-firing
      e.stopPropagation?.();
    }
  });

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
  };
};
